import React, { useState, useEffect, useCallback } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { API_URL } from "../../utils/config";
import AjudaCataleg from "./AjudaCataleg";
import { BotoAjuda } from "./AjudaModal";

function idTipus(t) {
  return t ? Number(t.idTipusDeMaterials ?? t.idTipusDeMaterial) : null;
}

function esActiu(v) {
  return v === true || v === 1 || v === "1";
}

function Detallstipusdematerials({
  mode,
  tipusDeMaterial,
  tipusLlista,
  onNavigate,
  onCancel,
  onSave,
  onCanviarMode,
  onDeleted,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  TeclaEscEnter(onCancel, null);

  const isReadOnly = mode === "consultar" || mode === "esborrar";
  const [tipus, setTipus] = useState("");
  const [categoria, setCategoria] = useState(null);
  const [actiu, setActiu] = useState(true);
  const [categories, setCategories] = useState([]);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const resetForm = useCallback(() => {
    setTipus("");
    setCategoria(null);
    setActiu(true);
  }, []);

  const llista = Array.isArray(tipusLlista) ? tipusLlista : [];
  const indexActual = tipusDeMaterial
    ? llista.findIndex((t) => idTipus(t) === idTipus(tipusDeMaterial))
    : -1;
  const potNavegar = ["consultar", "modificar"].includes(mode) && llista.length > 1 && indexActual >= 0;

  const anarAnterior = () => {
    if (!potNavegar) return;
    const nouIndex = indexActual === 0 ? llista.length - 1 : indexActual - 1;
    onNavigate?.(llista[nouIndex]);
  };

  const anarSeguent = () => {
    if (!potNavegar) return;
    const nouIndex = indexActual === llista.length - 1 ? 0 : indexActual + 1;
    onNavigate?.(llista[nouIndex]);
  };

  useEffect(() => {
    if (tipusDeMaterial && (mode === "consultar" || mode === "modificar" || mode === "esborrar")) {
      setTipus(tipusDeMaterial.Tipus || "");
      setCategoria(
        tipusDeMaterial.Categories_idCategories != null && tipusDeMaterial.Categories_idCategories !== ""
          ? Number(tipusDeMaterial.Categories_idCategories)
          : null
      );
      setActiu(esActiu(tipusDeMaterial.Actiu));
    } else if (mode === "nou") {
      resetForm();
    }
    setMenuAccions(false);
  }, [tipusDeMaterial, mode, resetForm]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/Categories.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada?.IdEmpresa }),
        });
        const result = await response.json();
        if (result.success) setCategories(result.categories || []);
      } catch (error) {
        console.error("Error carregant categories:", error);
      }
    };
    fetchCategories();
  }, [empresaSeleccionada]);

  const categoriaNom =
    categories.find((c) => Number(c.idCategories) === Number(categoria))?.Categoria ||
    tipusDeMaterial?.Categoria ||
    "—";

  const titol =
    mode === "nou"
      ? "Nou tipus de material"
      : mode === "esborrar"
      ? "Esborrar tipus de material"
      : mode === "modificar"
      ? "Modificar tipus de material"
      : "Tipus de materials · Fitxa";

  const subtitol =
    mode === "nou"
      ? "Indica la categoria, el nom del tipus i si està actiu."
      : mode === "esborrar"
      ? "No es podrà esborrar si té materials associats."
      : mode === "modificar"
      ? "Edita el tipus i la seva categoria."
      : "Consulta la informació del tipus de material.";

  const validarFormulari = () => {
    if (!tipus.trim()) {
      alert("El camp 'Tipus' no pot estar buit.");
      return false;
    }
    if (tipus.trim().length > 100) {
      alert("El nom del tipus no pot superar 100 caràcters.");
      return false;
    }
    if (!categoria) {
      alert("Has de seleccionar una categoria.");
      return false;
    }
    return true;
  };

  const payloadActualitzat = () => ({
    ...tipusDeMaterial,
    idTipusDeMaterials: idTipus(tipusDeMaterial),
    Tipus: tipus.trim(),
    Categories_idCategories: categoria,
    Categoria: categoriaNom === "—" ? "" : categoriaNom,
    Actiu: actiu ? 1 : 0,
  });

  const handleSubmit = async () => {
    if (mode !== "esborrar" && !validarFormulari()) return;

    try {
      if (mode === "nou") {
        const response = await fetch(`${API_URL}/Noutipusdematerial.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada?.IdEmpresa,
            Tipus: tipus.trim(),
            Categories_idCategories: categoria,
            Actiu: actiu ? 1 : 0,
          }),
        });
        const data = await response.json();
        if (data.success) {
          onSave?.({
            idTipusDeMaterials: data.idTipusDeMaterial,
            Tipus: tipus.trim(),
            Categories_idCategories: categoria,
            Categoria: categoriaNom === "—" ? "" : categoriaNom,
            Actiu: actiu ? 1 : 0,
          });
        } else {
          alert(`Error creant Tipus de Material: ${data.message}`);
        }
      } else if (mode === "modificar") {
        const response = await fetch(`${API_URL}/Modificatipusdematerial.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idTipusDeMaterials: idTipus(tipusDeMaterial),
            Tipus: tipus.trim(),
            Categories_idCategories: categoria,
            Actiu: actiu ? 1 : 0,
          }),
        });
        const data = await response.json();
        if (data.success) {
          onSave?.(payloadActualitzat());
        } else {
          alert(`Error modificant Tipus de Material: ${data.message}`);
        }
      } else if (mode === "esborrar") {
        const response = await fetch(`${API_URL}/Esborratipusdematerial.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idTipusDeMaterials: idTipus(tipusDeMaterial),
          }),
        });
        const data = await response.json();
        if (data.success) {
          onDeleted?.();
        } else {
          alert(`Error esborrant Tipus de Material: ${data.message}`);
        }
      }
    } catch (error) {
      console.error("Error en handleSubmit TipusDeMaterial:", error);
      alert("S'ha produït un error en el procés.");
    }
  };

  return (
    <form className="dv2-entity" onSubmit={(e) => e.preventDefault()}>
      {mostraAjuda && <AjudaCataleg pestanya="tipusmaterials" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "esborrar" ? "🗑️" : "🧩"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">{subtitol}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Tipus de materials" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && tincPermis(rolUsuari, "MODIFICAR") && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={() => onCanviarMode?.("modificar")}
            >
              Modificar
            </button>
          )}
          {mode === "consultar" && tincPermis(rolUsuari, "ESBORRAR") && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      onCanviarMode?.("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
          {mode === "nou" || mode === "modificar" ? (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar
            </button>
          ) : null}
          {mode === "esborrar" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
              Esborrar tipus
            </button>
          )}
        </div>
      </div>

      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          Aquesta acció no es pot desfer. Si el tipus té materials associats, el servidor no el deixarà esborrar.
        </div>
      )}

      <div className="dv2-entity-body">
      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">Dades bàsiques</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <label className="dv2-field dv2-field--mig">
            <span className={`dv2-label ${isReadOnly ? "" : "dv2-required"}`}>Categoria</span>
            {isReadOnly ? (
              <span className="dv2-value">{categoriaNom}</span>
            ) : (
              <select
                className="dv2-select"
                value={categoria != null ? String(categoria) : ""}
                onChange={(e) => setCategoria(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Selecciona una categoria</option>
                {categories.map((cat) => (
                  <option key={cat.idCategories} value={String(cat.idCategories)}>
                    {cat.Categoria}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label className="dv2-field dv2-field--llarg">
            <span className={`dv2-label ${isReadOnly ? "" : "dv2-required"}`}>Tipus</span>
            {isReadOnly ? (
              <span className="dv2-value">{tipus || "—"}</span>
            ) : (
              <input
                type="text"
                className="dv2-input"
                value={tipus}
                maxLength={100}
                autoFocus
                onChange={(e) => setTipus(e.target.value)}
              />
            )}
          </label>
          <label className="dv2-field dv2-field--curt">
            <span className="dv2-label">Actiu</span>
            {isReadOnly ? (
              <span className={`dv2-chip ${actiu ? "dv2-chip--ok" : ""}`}>{actiu ? "Sí" : "No"}</span>
            ) : (
              <span className="dv2-toggleRow">
                <span className="dv2-toggle">
                  <input
                    type="checkbox"
                    checked={actiu}
                    onChange={(e) => setActiu(e.target.checked)}
                  />
                  <span className="dv2-toggle-track" />
                </span>
                {actiu ? "Sí" : "No"}
              </span>
            )}
          </label>
        </div>
      </section>
      </div>

      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anarAnterior} title="Anterior">
                {"\u2039"}
              </button>
              <span className="dv2-recordNav-info">
                {indexActual + 1} / {llista.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anarSeguent} title="Següent">
                {"\u203A"}
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" && (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar tipus
                </button>
              )}
              {mode !== "esborrar" && (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar tipus" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default Detallstipusdematerials;
